var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { DoubleSide, Mesh } from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { STLLoader } from './BaseLoaders/STLLoader';
import { OBJLoader } from './BaseLoaders/OBJLoader';
import { MTLLoader } from './BaseLoaders/MTLLoader';
import { Part3D } from '../Objects/Part3D';
import { DEFAULT_MESH_MATERIAL } from '../constants/constants';
import { isMesh } from '../utils/typeUtils';
export var FileExtension;
(function (FileExtension) {
    FileExtension["STL"] = "stl";
    FileExtension["OBJ"] = "obj";
    FileExtension["MTL"] = "mtl";
    FileExtension["GLTF"] = "gltf";
    FileExtension["GLB"] = "glb";
})(FileExtension || (FileExtension = {}));
export class Loader {
    constructor(defaultMaterial = DEFAULT_MESH_MATERIAL) {
        this.defaultMaterial = defaultMaterial;
    }
    /**
     *
     * @param url url of file with geometry
     * @param filename name of geometry file, used to extract extension of a file
     * @param materialFileUrl optional material file, used only in a couple with obj file
     * @returns loaded geometry wrapped in `CustomObject3D`
     */
    loadFileByURL(url, filename, materialFileUrl) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const extension = (_a = filename.split('.').pop()) === null || _a === void 0 ? void 0 : _a.toLowerCase();
            if (!extension) {
                throw new Error('Unable to determine extension of a file');
            }
            switch (extension) {
                case FileExtension.STL: {
                    const loader = new STLLoader();
                    const geometry = yield loader.loadAsync(url);
                    geometry.computeVertexNormals();
                    const part = new Part3D(new Mesh(geometry, this.defaultMaterial.clone()));
                    this.makeMaterialsDoubleSided(part);
                    return part;
                }
                case FileExtension.OBJ: {
                    const loader = new OBJLoader();
                    if (materialFileUrl) {
                        const materialCreator = yield this.loadMaterialCreator(materialFileUrl);
                        materialCreator.preload();
                        loader.setMaterials(materialCreator);
                    }
                    const part = new Part3D(yield loader.loadAsync(url));
                    this.makeMaterialsDoubleSided(part);
                    return part;
                }
                case FileExtension.GLTF:
                case FileExtension.GLB: {
                    const loader = new GLTFLoader();
                    const gltf = yield loader.loadAsync(url);
                    const part = new Part3D(gltf.scene);
                    this.makeMaterialsDoubleSided(part);
                    return part;
                }
                default: {
                    console.warn('Unknown file format');
                    return null;
                }
            }
        });
    }
    loadMaterialCreator(url) {
        return __awaiter(this, void 0, void 0, function* () {
            const mtlLoader = new MTLLoader();
            return mtlLoader.loadAsync(url);
        });
    }
    makeMaterialsDoubleSided(object) {
        object.traverse((child) => {
            if (isMesh(child)) {
                // Compute vertex normals if not present for smooth shading
                if (!child.geometry.attributes.normal) {
                    child.geometry.computeVertexNormals();
                }
                if (Array.isArray(child.material)) {
                    child.material.forEach((mat) => {
                        mat.setValues({ side: DoubleSide });
                        mat.flatShading = false;
                    });
                }
                else {
                    child.material.setValues({ side: DoubleSide });
                    child.material.flatShading = false;
                }
            }
        });
    }
}
